import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Fab,
    Chip,
    Stack,
    Avatar,
    CircularProgress,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { collection, query, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { CRMEntry, CRMStage } from '../../../models/CRMEntry';

const STAGES: { id: CRMStage; emoji: string; title: string }[] = [
    { id: 'New Lead', emoji: '🟦', title: 'New Lead' },
    { id: 'Follow-Up Scheduled', emoji: '📅', title: 'Follow-Up' },
    { id: 'Proposal Sent', emoji: '📄', title: 'Proposal Sent' },
    { id: 'Contract Sent', emoji: '✍️', title: 'Contract Sent' },
    { id: 'Active Order', emoji: '✅', title: 'Active Order' },
    { id: 'Closed Won', emoji: '🟢', title: 'Closed Won' },
    { id: 'Closed Lost', emoji: '🔴', title: 'Closed Lost' },
];

interface CRMKanbanProps {
    onCreateEntry?: () => void;
}

export const CRMKanban: React.FC<CRMKanbanProps> = ({ onCreateEntry }) => {
    const navigate = useNavigate();
    const [entries, setEntries] = useState<CRMEntry[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load CRM entries from Firestore
    useEffect(() => {
        const q = query(collection(db, 'crmEntries'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            try {
                const newEntries: CRMEntry[] = [];
                snapshot.forEach((doc) => {
                    newEntries.push({ id: doc.id, ...doc.data() } as CRMEntry);
                });
                setEntries(newEntries);
                setLoading(false);
            } catch (err) {
                console.error('Error processing CRM entries:', err);
                setError('Failed to load CRM entries. Please try again.');
                setLoading(false);
            }
        }, (err) => {
            console.error('Error in Firestore subscription:', err);
            setError('Failed to subscribe to CRM updates. Please refresh the page.');
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter entries based on search query
    const filteredEntries = entries.filter((entry) =>
        entry.companyName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group entries by stage
    const entriesByStage = STAGES.reduce((acc, { id }) => {
        acc[id] = filteredEntries.filter((entry) => entry.stage === id);
        return acc;
    }, {} as Record<CRMStage, CRMEntry[]>);

    const handleDragEnd = async (result: any) => {
        if (!result.destination) return;

        const { draggableId, source, destination } = result;

        if (source.droppableId === destination.droppableId) return;

        try {
            const entryRef = doc(db, 'crmEntries', draggableId);
            await updateDoc(entryRef, {
                stage: destination.droppableId,
                lastActivityAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating entry stage:', error);
            setError('Failed to update entry stage. Please try again.');
        }
    };

    const handleCardClick = (entryId: string, event: React.MouseEvent) => {
        // Prevent click when dragging
        if (event.defaultPrevented) return;
        navigate(`/crm/${entryId}`);
    };

    const CRMCard: React.FC<{ entry: CRMEntry; index: number }> = ({ entry, index }) => (
        <Draggable draggableId={entry.id} index={index}>
            {(provided) => (
                <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={(e) => handleCardClick(entry.id, e)}
                    sx={{
                        mb: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: 3,
                        },
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {entry.companyName}
                        </Typography>

                        <Stack spacing={1}>
                            {entry.value && (
                                <Typography variant="body2" color="success.main">
                                    ${entry.value.toLocaleString()}
                                </Typography>
                            )}

                            {entry.assignedTo && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Avatar sx={{ width: 24, height: 24 }}>
                                        {entry.assignedTo.charAt(0)}
                                    </Avatar>
                                    <Typography variant="body2">
                                        {entry.assignedTo}
                                    </Typography>
                                </Stack>
                            )}

                            {entry.lastActivityAt && (
                                <Typography variant="caption" color="text.secondary">
                                    Last activity: {format(entry.lastActivityAt.toDate(), 'MMM d, yyyy')}
                                </Typography>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Draggable>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Search Bar */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by company name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    size="small"
                />
            </Box>

            {/* Kanban Board */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <Box
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        flexGrow: 1,
                        gap: 2,
                        p: 2,
                        minHeight: 0,
                    }}
                >
                    {STAGES.map(({ id, emoji, title }) => (
                        <Box
                            key={id}
                            sx={{
                                width: 300,
                                flexShrink: 0,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    p: 1,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 1,
                                    mb: 2,
                                }}
                            >
                                {emoji} {title} ({entriesByStage[id]?.length || 0})
                            </Typography>

                            <Droppable droppableId={id}>
                                {(provided) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{
                                            flexGrow: 1,
                                            minHeight: 100,
                                            backgroundColor: 'background.default',
                                            borderRadius: 1,
                                            p: 1,
                                        }}
                                    >
                                        {entriesByStage[id]?.map((entry, index) => (
                                            <CRMCard key={entry.id} entry={entry} index={index} />
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    ))}
                </Box>
            </DragDropContext>

            {/* FAB for creating new entry */}
            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={onCreateEntry}
            >
                <AddIcon />
            </Fab>
        </Box>
    );
}; 